/**
 * The provider-neutral half of the "avatar presents the session document"
 * capability: tool names, the wording the model reads, and the shape of the
 * answers our browser handlers give back.
 *
 * D-ID and Kaltura both implement this capability with client tools, but with
 * different wire protocols (D-ID returns a JSON string from a registered
 * handler; Kaltura ACKs a blocking invocation). Everything that is *not* the
 * protocol lives here so the two can never drift apart in wording again.
 * Provider-specific files own only their own payload shape and tool ids.
 */
import type { DocumentControl } from "@/components/session/DisplayDocument";

export const PRESENT_DOCUMENT_TOOL = "present_document";
export const CLOSE_DOCUMENT_TOOL = "close_document";
export const SHOW_PAGE_TOOL = "show_page";
export const READ_PAGE_TOOL = "read_current_page";

/** Descriptions sent upstream with each tool, keyed by tool name. */
export const PRESENTATION_TOOL_DESCRIPTIONS: Record<string, string> = {
  [PRESENT_DOCUMENT_TOOL]:
    "Show the learner the document for this session on their screen. Use this when the learner asks to see it, or right before you refer to its contents.",
  [CLOSE_DOCUMENT_TOOL]:
    "Remove the document from the learner's screen and return to the full-screen avatar view. Use this once the discussion moves on or the learner asks to close it.",
  [SHOW_PAGE_TOOL]:
    "Turn the presented document to a specific page for the learner. Shows the document first if it is not on screen yet. Returns the page it landed on and the total page count.",
  [READ_PAGE_TOOL]:
    "Read the text of the page the learner is currently looking at, plus its page number and the total page count. Always call this before saying anything about what the document shows, because the learner can turn pages themselves at any time. If the result includes learner_moved, the learner has moved since your last action.",
};

/** The `show_page` argument, described once for both providers' arg shapes. */
export const SHOW_PAGE_ARG = {
  name: "page",
  prompt: "The 1-based page number to display.",
};

/**
 * Instruction lines appended to the agent's system prompt while the capability
 * is on. Tool descriptions alone do not reliably drive the calls — the model
 * must also know the document starts hidden.
 */
export function presentationInstruction(pageable: boolean): string {
  const base =
    "The learner cannot see the session document until you show it. Use the present_document tool to bring it up on their screen when it becomes relevant or they ask for it, and the close_document tool to put it away when the conversation moves on.";
  if (!pageable) return base;
  return (
    base +
    " The document has pages. Use show_page to turn to a specific 1-based page. Before you say anything about what the document shows, always call read_current_page: the learner can turn pages themselves at any time, so a page number from an earlier turn is never reliable. If a tool result contains learner_moved, the learner has navigated on their own — acknowledge the page they are actually on and discuss that page instead of the one you last turned to."
  );
}

/** What a paging/read handler answers with. Serialization is the caller's job. */
export type PagingToolResult = Record<string, unknown>;

/**
 * Turn to a page and describe where we landed. `learner_moved` tells the model
 * the learner had navigated on their own, so it stops trusting its last page.
 */
export function showPageResult(
  control: DocumentControl | null,
  requestedPage: number,
): PagingToolResult {
  if (!control || control.kind !== "pdf") {
    return { error: "This document has no pages that can be turned." };
  }
  const at = control.goToPage(Number.isFinite(requestedPage) ? requestedPage : 1);
  return {
    shown: true,
    page: at.page,
    total_pages: at.total,
    ...(at.learnerMoved
      ? {
          learner_moved: true,
          note: `Before this, the learner had turned the document themselves; it is now on page ${at.page}.`,
        }
      : {}),
  };
}

/** Read the page the learner is actually looking at right now. */
export async function readPageResult(
  control: DocumentControl | null,
): Promise<PagingToolResult> {
  if (!control || control.kind !== "pdf") {
    return { error: "The document currently on screen cannot be read." };
  }
  const read = await control.readCurrentPage();
  return {
    page: read.page,
    total_pages: read.total,
    text: read.text,
    ...(read.learnerMoved
      ? {
          learner_moved: true,
          note: `The learner turned to page ${read.page} themselves since your last action — talk about this page, not the one you last mentioned.`,
        }
      : {}),
  };
}

/** How long a browser-side document tool may take before we answer anyway. */
export const TOOL_ANSWER_TIMEOUT_MS = 4000;

/**
 * Never let a document tool call hang. The model waits for our answer, so a
 * handler that throws or stalls (viewer still mounting, a page read that never
 * settles) used to leave the avatar mute for the rest of the turn. This races
 * the handler against a bounded timeout and always resolves with something the
 * model can act on. `onOutcome` receives the outcome for logging.
 */
export async function withToolAnswer(
  name: string,
  fn: () => Promise<PagingToolResult> | PagingToolResult,
  onOutcome?: (info: { tool: string; outcome: "ok" | "timeout" | "error"; detail?: string }) => void,
): Promise<PagingToolResult> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<PagingToolResult>((resolve) => {
      timer = setTimeout(
        () =>
          resolve({
            error: "timeout",
            note: "The document view did not respond in time. Tell the learner briefly and try again if it still matters.",
            __timedOut: true,
          }),
        TOOL_ANSWER_TIMEOUT_MS,
      );
    });
    const result = await Promise.race([Promise.resolve().then(fn), timeout]);
    if ((result as { __timedOut?: boolean }).__timedOut) {
      onOutcome?.({ tool: name, outcome: "timeout" });
      const { __timedOut: _drop, ...rest } = result as Record<string, unknown>;
      return rest;
    }
    onOutcome?.({ tool: name, outcome: "ok" });
    return result;
  } catch (e) {
    const detail = (e as Error)?.message || String(e);
    onOutcome?.({ tool: name, outcome: "error", detail });
    return {
      error: "failed",
      note: "The document view could not complete that action right now.",
    };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Wait (briefly) for the viewer to actually be mounted and pageable before
 * turning or reading a page — the document may have been hidden a moment ago.
 */
export async function waitForPageableControl(
  read: () => DocumentControl | null,
  timeoutMs = 1200,
): Promise<DocumentControl | null> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const control = read();
    if (control && control.kind === "pdf" && control.currentPage() > 0) return control;
    if (Date.now() >= deadline) return control;
    await new Promise((r) => setTimeout(r, 60));
  }
}
